import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "@redotech/redo-views-common/advanced-filters/boolean-filter";
import {
  DateFilterOperator,
  KnownDateFilterTimeFrame,
} from "@redotech/redo-views-common/advanced-filters/date-filter";
import { ExistsFilterOperator } from "@redotech/redo-views-common/advanced-filters/exists-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { StringFilterOperator } from "@redotech/redo-views-common/advanced-filters/string-filter";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RateProvider } from "../outbound-labels/outbound-labels";
import { AdvancedFilterData } from "../views/views";
import { FulfillmentGroupStatus } from "./fulfillment-group-status";

// The EOD manifest should always look for shipments starting at 5 AM UTC because we auto manifest at 4:30 AM UTC
export function getMostRecentManifestCutoff(): Temporal.Instant {
  const now = Temporal.Now.zonedDateTimeISO("UTC");

  const todayFiveAm = now.with({
    hour: 5,
    minute: 0,
    second: 0,
    millisecond: 0,
    microsecond: 0,
    nanosecond: 0,
  });

  const target =
    now.epochNanoseconds < todayFiveAm.epochNanoseconds
      ? todayFiveAm.subtract({ days: 1 })
      : todayFiveAm;

  return target.toInstant();
}

export function getDynamicEndOfDayManifestFilters({
  carrier,
  address,
}: {
  carrier?: Carriers;
  address?: string;
}): AdvancedFilterData[] {
  const filters = getStaticEndOfDayManifestFilters();

  if (carrier) {
    // Our shipping provider (easypost) has multiple names for USPS, depending on what type of account it's under
    // The use just wants to know that we are manifesting USPS shipments, they don't need to know about the different names
    const carriersToSearch =
      carrier === Carriers.USPS
        ? [Carriers.USPS, Carriers.USPS_SHIP]
        : [carrier];
    filters.push({
      name: "boughtRateCarrier",
      type: AdvancedFilterType.ARRAY_TO_STRING,
      value: carriersToSearch,
      operator: ArrayStringFilterOperator.ANY_OF,
    });

    // Easypost does not support pre defined USPS parcels Flat, Card, and Letter
    // https://support.easypost.com/hc/en-us/articles/360041673351-USPS-Manifesting
    // Pre defined packages here: https://docs.easypost.com/carriers/usps-guide#predefined-packages
    if (carrier === Carriers.USPS || carrier === Carriers.USPS_SHIP) {
      // For USPS, we need conditional logic:
      // - If parcel type is 'carrier' and servie is Flat, exclude Card/Letter/Flat pre defined parcels
      // - If parcel type is 'custom', allow any name/service
      // This will be handled by a special compound filter in the search function
      filters.push({
        name: "uspsParcelConstraints",
        type: AdvancedFilterType.STRING,
        value: "NO VALUE",
        operator: StringFilterOperator.EQUALS,
      });
    }

    if (carrier === Carriers.CANADA_POST) {
      filters.push({
        name: "canadaPostManifestRequired",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      });
    }
  }

  if (address) {
    filters.push({
      name: "fulfillmentLocation",
      type: AdvancedFilterType.STRING,
      value: address,
      operator: StringFilterOperator.EQUALS,
    });
  }

  if (process.env.NODE_ENV === "production") {
    filters.push({
      name: "shipmentRateMode",
      type: AdvancedFilterType.STRING,
      value: "production",
      operator: StringFilterOperator.EQUALS,
    });
  } else {
    filters.push({
      name: "shipmentRateMode",
      type: AdvancedFilterType.STRING,
      value: "test",
      operator: StringFilterOperator.EQUALS,
    });
  }

  // Tiktok can provide USPS labels, but we can't manifest them through
  // the API, so we need to exclude them from the manifest.
  filters.push({
    name: "boughtRateProvider",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [RateProvider.TIKTOK],
    operator: ArrayStringFilterOperator.NONE_OF,
  });

  return filters;
}

export function getStaticEndOfDayManifestFilters(): AdvancedFilterData[] {
  return [
    {
      name: "shipmentVoided",
      type: AdvancedFilterType.BOOLEAN,
      value: false,
      operator: BooleanFilterOperator.IS,
    },
    {
      type: AdvancedFilterType.EXISTS,
      name: "shipmentScanFormBatchState",
      value: true,
      operator: ExistsFilterOperator.NOT_EXISTS,
    },
    {
      name: "shipmentScanFormId",
      type: AdvancedFilterType.EXISTS,
      value: true,
      operator: ExistsFilterOperator.NOT_EXISTS,
    },
    {
      name: "status",
      type: AdvancedFilterType.STRING,
      value: FulfillmentGroupStatus.Closed,
      operator: StringFilterOperator.EQUALS,
    },
    {
      name: "labelDate",
      type: AdvancedFilterType.DATE,
      value: KnownDateFilterTimeFrame.CUSTOM,
      customDate: [
        new Date(getMostRecentManifestCutoff().toString()),
        new Date(),
      ],
      operator: DateFilterOperator.WITHIN,
    },
  ];
}

export enum ScanFormStatus {
  Creating = "creating",
  Created = "created",
  Error = "failed",
}

export enum BatchState {
  Creating = "creating",
  CreationFailed = "creation_failed",
  Created = "created",
  Purchasing = "purchasing",
  PurchaseFailed = "purchase_failed",
  Purchased = "purchased",
  LabelGenerating = "label_generating",
  LabelGenerated = "label_generated",
}

export function isBatchEventActionState(state: BatchState): boolean {
  const actionStates = [
    BatchState.LabelGenerating,
    BatchState.LabelGenerated,
    BatchState.Created,
    BatchState.Purchased,
  ];
  return actionStates.includes(state);
}

const batchEventSchema_ = z.object({
  id: z.string(),
  state: createEnum(BatchState, { enumName: "BatchState" }),
});
export const batchEventSchema: z.ZodType<BatchEvent> = batchEventSchema_;

export interface BatchEvent extends z.infer<typeof batchEventSchema_> {}

const scanFormStatusInfoSchema_ = z.object({
  id: z.string(),
  status: createEnum(ScanFormStatus, { enumName: "ScanFormStatus" }),
});
export const scanFormStatusInfoSchema: z.ZodType<ScanFormStatusInfo> =
  scanFormStatusInfoSchema_;

export interface ScanFormStatusInfo extends z.infer<
  typeof scanFormStatusInfoSchema_
> {}

const shippingProviderScanForm_ = z.object({
  id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  tracking_codes: z.array(z.string()).nullish(),
  address: z
    .object({
      id: z.string(),
      name: z.string().nullish(),
      street1: z.string().nullish(),
      street2: z.string().nullish(),
      city: z.string().nullish(),
      state: z.string().nullish(),
      zip: z.string().nullish(),
      country: z.string().nullish(),
      phone: z.string().nullish(),
      email: z.string().nullish(),
      carrier_facility: z.string().nullish(),
      residential: z.boolean().nullish(),
      federal_tax_id: z.string().nullish(),
      state_tax_id: z.string().nullish(),
    })
    .nullish(),
  status: createEnum(ScanFormStatus, { enumName: "ScanFormStatus" }),
  message: z.string().nullish(),
  form_url: z.string().nullish(),
  form_file_type: z.string().nullish(),
  batch_id: z.string().nullish(),
});
export const shippingProviderScanForm: z.ZodType<ShippingProviderScanForm> =
  shippingProviderScanForm_;

export interface ShippingProviderScanForm extends z.infer<
  typeof shippingProviderScanForm_
> {}

const RedoScanFormSchema_ = z.object({
  id: z.string(),
  externalCreatedAt: z.date(),
  externalUpdatedAt: z.date(),
  trackingCodes: z.array(z.string()),
  address: z
    .object({
      id: z.string(),
      name: z.string().nullish(),
      street1: z.string().nullish(),
      street2: z.string().nullish(),
      city: z.string().nullish(),
      state: z.string().nullish(),
      zip: z.string().nullish(),
      country: z.string().nullish(),
      phone: z.string().nullish(),
      email: z.string().nullish(),
      carrierFacility: z.string().nullish(),
      residential: z.boolean().nullish(),
      federalTaxId: z.string().nullish(),
      stateTaxId: z.string().nullish(),
    })
    .nullish(),
  status: createEnum(ScanFormStatus, { enumName: "ScanFormStatus" }),
  message: z.string().nullish(),
  formUrl: z.string(),
  formUrl4x6: z.string().nullish(),
  formFileType: z.string().nullish(),
  batchId: z.string().nullish(),
  confirmation: z.string().nullish(),
});
export const RedoScanFormSchema: z.ZodType<RedoScanForm> = RedoScanFormSchema_;

export interface RedoScanForm extends z.infer<typeof RedoScanFormSchema_> {}

export enum ManifestType {
  SYNC = "synchronous",
  ASYNC = "asynchronous",
}

const EndOfDayManifestSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  manifestType: createEnum(ManifestType, { enumName: "ManifestType" }).default(
    ManifestType.ASYNC,
  ),
  carrier: createEnum(Carriers, { enumName: "Carriers" }),
  carrierAccountId: z.string().nullish(),
  provider: createEnum(RateProvider, { enumName: "RateProvider" }).nullish(),
  pickupId: z.string().nullish(),
  groupId: z.string().nullish(),
  fulfillmentGroupIds: z.array(zExt.objectId()),
  location: z.string().nullish(),
  batch: z
    .object({
      id: z.string(),
      state: createEnum(BatchState, { enumName: "BatchState" }),
    })
    .nullish(),
  scanForm: RedoScanFormSchema_.nullish(),
  scanFormCreationRequested: z.boolean().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const EndOfDayManifestSchema: z.ZodType<EndOfDayManifest> =
  EndOfDayManifestSchema_;

export interface EndOfDayManifest extends z.infer<
  typeof EndOfDayManifestSchema_
> {}

const CreateEndOfDayManifestSchema_ = EndOfDayManifestSchema_.omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
});
export const CreateEndOfDayManifestSchema: z.ZodType<CreateEndOfDayManifest> =
  CreateEndOfDayManifestSchema_;

export interface CreateEndOfDayManifest extends z.infer<
  typeof CreateEndOfDayManifestSchema_
> {}
